/**
 * =============================================================================
 * Componente: AffiliateProtectedRoute
 * Versão: 2.0.0 (Fase 4 — Modelo N:N)
 * Descrição: Guard que protege rotas /afiliado/* exigindo:
 *   1. Sessão Supabase Auth ativa
 *   2. Registro válido em affiliates vinculado ao user
 *   3. Pelo menos UMA membership com status='active'
 *      (apenas pending/banidas → tela de espera ou bloqueio)
 *
 * Mudanças v2.0.0:
 *   - Não existe mais `affiliate.status` real na tabela. Avaliamos a lista
 *     `affiliate.memberships` para decidir o acesso:
 *       • alguma 'active' → entra
 *       • só 'pending'    → tela "aguardando aprovação"
 *       • só 'suspended' / 'banned' / 'rejected' → bloqueio na tela de login
 *       • nenhuma         → bloqueio com motivo "no_affiliate"
 * =============================================================================
 */
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAffiliateAuth } from '@/hooks/useAffiliateAuth';
import {
  affiliateLoginPath,
  affiliateAwaitingPath,
  setLastAffiliateTenantSlug,
  useAffiliateTenantSlug,
} from '@/lib/affiliate-urls';

interface AffiliateProtectedRouteProps {
  children: React.ReactNode;
  /** Permite acesso mesmo sem nenhuma adesão ativa (apenas pending). */
  allowPending?: boolean;
}

export function AffiliateProtectedRoute({
  children,
  allowPending = false,
}: AffiliateProtectedRouteProps) {
  const { ready, user, affiliate, availableAffiliates } = useAffiliateAuth();
  const location = useLocation();
  // Slug do tenant atual: rota → query → último em cache. Mantém URLs por-tenant.
  const tenantSlug = useAffiliateTenantSlug();
  const loginTo = affiliateLoginPath(tenantSlug);
  const awaitingTo = affiliateAwaitingPath(tenantSlug);

  // Loading — aguarda checagem de sessão + busca de afiliados/memberships
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Não autenticado
  if (!user) {
    return (
      <Navigate
        to={loginTo}
        replace
        state={{ from: location.pathname }}
      />
    );
  }

  // Autenticado mas não tem registro de afiliado
  if (!affiliate) {
    return (
      <Navigate
        to={loginTo}
        replace
        state={{ error: 'no_affiliate' }}
      />
    );
  }

  // Multi-tenant: se há mais de uma conta vinculada, força seleção explícita
  // antes de liberar o painel. A página /afiliado/selecionar-conta NÃO está
  // sob este guard, então não há loop.
  const activeId = typeof window !== 'undefined'
    ? localStorage.getItem('currentAffiliateId')
    : null;
  if (
    availableAffiliates.length > 1 &&
    (!activeId || !availableAffiliates.some((a) => a.id === activeId))
  ) {
    return <Navigate to="/afiliado/selecionar-conta" replace />;
  }

  // Avaliar memberships
  const memberships = affiliate.memberships ?? [];
  const hasActive = memberships.some((m) => m.status === 'active');
  const hasPending = memberships.some((m) => m.status === 'pending');
  const onlyBlocked =
    memberships.length > 0 &&
    memberships.every((m) =>
      ['suspended', 'banned', 'rejected', 'inactive'].includes(m.status),
    );

  // Caso especial: tela de aguardando aprovação
  // [v2.0.1] Se já existe alguma adesão ATIVA, sai da tela de espera para o painel.
  // Sem isso, o usuário aprovado continuava preso em /afiliado/aguardando-aprovacao
  // mesmo após o refresh trazer status='active'.
  if (allowPending && hasActive) {
    // Persiste o slug do tenant atual para que navegações públicas futuras
    // (logout, esqueci-senha, etc.) continuem usando a URL canônica /afiliado/<slug>/...
    if (tenantSlug) setLastAffiliateTenantSlug(tenantSlug);
    return <Navigate to="/afiliado" replace />;
  }
  if (allowPending && hasPending) {
    return <>{children}</>;
  }

  // Tem ao menos uma adesão ativa → libera
  if (hasActive) {
    return <>{children}</>;
  }

  // Só pending → redireciona para tela de aguardando
  if (hasPending) {
    return <Navigate to={awaitingTo} replace />;
  }

  // Todas bloqueadas
  if (onlyBlocked) {
    const blockedStatus = memberships[0].status;
    return (
      <Navigate
        to={loginTo}
        replace
        state={{ error: blockedStatus }}
      />
    );
  }

  // Sem memberships
  return (
    <Navigate
      to={loginTo}
      replace
      state={{ error: 'no_membership' }}
    />
  );
}
